import React, { useState, useEffect } from 'react';
import { useIntervalWhen } from 'rooks';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import styled from '@emotion/styled';
import '../sass/App.scss';

import CONST from '../constants';
import {
    MdOutlineKeyboardArrowLeft as IconSlideLeft,
    MdOutlineKeyboardArrowRight as IconSlideRight
} from 'react-icons/md';

const Container = styled.div`
    display: inline-block;
    position: relative;
    overflow: hidden;

    & > div.carousel-content {
        position: absolute;
        background-position-x: center;
        background-position-y: top;
        background-size: cover;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;

        &.slide-next-enter {
            transform: translateX(100%);
        }
        &.slide-next-enter-active {
            transform: translateX(0);
            transition: transform 400ms ease;
        }
        &.slide-next-exit {
            transform: translateX(0);
        }
        &.slide-next-exit-active {
            transform: translateX(-100%);
            transition: all 400ms ease;
        }

        &.slide-prev-enter {
            transform: translateX(-100%);
        }
        &.slide-prev-enter-active {
            transform: translateX(0);
            transition: transform 400ms ease;
        }
        &.slide-prev-exit {
            transform: translateX(0);
        }
        &.slide-prev-exit-active {
            transform: translateX(100%);
            transition: all 400ms ease;
        }
    }
`;

const ArrowContainer = styled.a`
    display: inline-block;
    position: absolute;
    top: 50%;
    width: 15%;
    aspect-ratio: 1 / 1;
    background: none;
    border-radius: 50%;
    cursor: pointer;
    transform: translateY(-50%);
    transition: opacity 300ms ease;

    @media screen and (min-width: ${CONST.SCREEN.BREAKPOINT.SM}px) {
        width: 10%;
        background: ${CONST.COLOR.BG};
        opacity: 0.5;

        &:hover {
            opacity: 1;
        }
    }

    &.left {
        left: 0;

        @media screen and (min-width: ${CONST.SCREEN.BREAKPOINT.SM}px) {
            left: 2%;
        }
    }
    &.right {
        right: 0;

        @media screen and (min-width: ${CONST.SCREEN.BREAKPOINT.SM}px) {
            right: 2%;
        }
    }

    & > svg {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 100%;
        height: 100%;
        color: ${CONST.COLOR.THEME};
        transform: translate(-50%, -50%);

        @media screen and (min-width: ${CONST.SCREEN.BREAKPOINT.SM}px) {
            color: ${CONST.COLOR.THEME};
        }
    }
`;

const PositionContainer = styled.div`
    position: absolute;
    left: 50%;
    bottom: 2%;
    transform: translateX(-50%);
`;

const PositionIndicator = styled.a`
    display: inline-block;
    position: relative;
    aspect-ratio: 1 / 1;
    width: 5vw;
    margin: 0 1vw;
    background: ${CONST.COLOR.BG};
    border-radius: 50%;
    opacity: 0.75;
    vertical-align: middle;

    @media screen and (min-width: ${CONST.SCREEN.BREAKPOINT.SM}px) {
        width: 1.5vw;
        margin: 0 0.25vw;
        cursor: pointer;

        &:hover {
            opacity: 1;
        }
    }

    @media screen and (min-width: ${CONST.SCREEN.BREAKPOINT.XL}px) {
        width: ${CONST.SCREEN.BREAKPOINT.XL * 0.015}px;
        margin: 0 ${CONST.SCREEN.BREAKPOINT.XL * 0.0025}px;
    }

    &.active {
        &::after {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            aspect-ratio: 1 / 1;
            width: 50%;
            background: ${CONST.COLOR.THEME};
            border-radius: 50%;
            opacity: 0.75;
            transform: translate(-50%, -50%);

            @media screen and (min-width: ${CONST.SCREEN.BREAKPOINT.SM}px) {
                width: 65%;
            }
        }
    }
`;

const TextContainer = styled.div`
    display: block;
    position: relative;
    padding: 5vw 0;
    background: ${CONST.COLOR.BG}BD;

    @media screen and (min-width: ${CONST.SCREEN.BREAKPOINT.SM}px) {
        padding: 2.5vw 0;
        background: ${CONST.COLOR.BG}BD;
    }

    @media screen and (min-width: ${CONST.SCREEN.BREAKPOINT.XL}px) {
        padding: ${CONST.SCREEN.BREAKPOINT.XL * 0.025}px 0;
    }

    & > p {
        margin: 0;
        color: ${CONST.COLOR.THEME};
        font-size: 4vw;
        font-weight: bold;
        text-align: center;

        @media screen and (min-width: ${CONST.SCREEN.BREAKPOINT.SM}px) {
            font-size: 2.5vw;
        }

        @media screen and (min-width: ${CONST.SCREEN.BREAKPOINT.XL}px) {
            font-size: ${CONST.SCREEN.BREAKPOINT.XL * 0.025}px;
        }
    }
`;

// .image = image src
// .text = text description
const Carousel = ({ slides = [], ...props }) => {
    /**************************************************************
     * HOOKS
     *************************************************************/

    const [slideIndex, setSlideIndex] = useState(0);
    const [animation, setAnimation] = useState(null);
    const [animateIndex, setAnimateIndex] = useState(null);
    const [timerState, setTimerState] = useState(true);

    useEffect(() => {
        if (animateIndex === null) {
            return;
        }

        setSlideIndex(animateIndex);
    }, [animateIndex]);

    useEffect(() => {
        setTimerState(true);
    }, [timerState]);

    useIntervalWhen(() => {
        handleArrowClick(true);
    }, 5000, timerState, false);

    /**************************************************************
     * CORE
     *************************************************************/

    const handleArrowClick = isNext => {
        if (isNext) {
            if (slideIndex === slides.length - 1) {
                handlePositionClick(0, 'slide-next');
                return;
            }

            handlePositionClick(slideIndex + 1, 'slide-next');
        } else {
            if (slideIndex === 0) {
                handlePositionClick(slides.length - 1, 'slide-prev');
                return;
            }

            handlePositionClick(slideIndex - 1, 'slide-prev');
        }
    }

    const handlePositionClick = (index, animation = null) => {
        if (index === slideIndex) {
            return;
        }

        if (animation === null) {
            if (index > slideIndex) {
                setAnimation('slide-next');
            } else {
                setAnimation('slide-prev');
            }
        } else {
            setAnimation(animation);
        }

        setAnimateIndex(index);
        setTimerState(false);
    }

    return (
        <Container {...props}>
            <TransitionGroup component={null}>
                <CSSTransition
                    key={slideIndex}
                    timeout={400}
                    classNames={animation}
                >
                    <React.Fragment>
                        <div className='carousel-content' style={{ backgroundImage: `url(${slides[slideIndex].image})` }}>
                            <TextContainer className='header'><p>{slides[slideIndex].text}</p></TextContainer>
                        </div>
                    </React.Fragment>

                </CSSTransition>
            </TransitionGroup>
            <ArrowContainer className='arrow left' onClick={() => handleArrowClick(false)}><IconSlideLeft /></ArrowContainer>
            <ArrowContainer className='arrow right' onClick={() => handleArrowClick(true)}><IconSlideRight /></ArrowContainer>
            <PositionContainer>
                {slides.map((slide, index) => (
                    <PositionIndicator key={`indicator_${index}`} className={index === slideIndex ? 'active' : null} onClick={() => handlePositionClick(index)} />
                ))}
            </PositionContainer>
        </Container >
    );
}

export default Carousel;
